import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  User,
  LogOut,
  Menu,
  X,
  Home,
  GraduationCap,
  Sparkles,
  BookOpen,
  Users,
  ChevronLeft,
  Star,
  LayoutDashboard,
} from 'lucide-react';
import { BRAND_NAME, BRAND_TAGLINE, logoImage } from '../../config/brand';
import ThemeToggle from './ThemeToggle';

type NavLinkItem = {
  type: 'link';
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  matchPrefix?: boolean;
};

type NavButtonItem = {
  type: 'button';
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  sectionId?: string;
};

type NavItem = NavLinkItem | NavButtonItem;

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fix hydration mismatch: only render auth-dependent content after mount
  const [mounted, setMounted] = useState(false);

  const isHomePage = location.pathname === '/';
  const isHeroOverlay = isHomePage && !isScrolled;
  const isRegisterPage = location.pathname === '/register';

  const isCurrentPath = (path: string, matchPrefix = false) =>
    matchPrefix ? location.pathname.startsWith(path) : location.pathname === path;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [isAuthenticated, currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const section = document.getElementById(sectionId);
      if (section && location.pathname === '/') {
        section.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (location.pathname !== '/') {
        window.location.href = `/#${sectionId}`;
        return;
      }
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    },
    [location.pathname, navigate],
  );

  const guestNavItems: NavItem[] = [
    { type: 'link', to: '/', label: 'الرئيسية', icon: Home },
    {
      type: 'button',
      label: 'الكورسات',
      icon: BookOpen,
      sectionId: 'suggested-courses-section',
      onClick: () => scrollToSection('suggested-courses-section'),
    },
    {
      type: 'button',
      label: 'المدرسون',
      icon: Users,
      sectionId: 'teachers-section',
      onClick: () => scrollToSection('teachers-section'),
    },
    {
      type: 'button',
      label: 'لماذا نحن',
      icon: Star,
      sectionId: 'why-choose-section',
      onClick: () => scrollToSection('why-choose-section'),
    },
  ];

  const authNavItems: NavItem[] = [
    {
      type: 'link',
      to: '/student-portal',
      label: 'بوابة الطالب',
      icon: LayoutDashboard,
      matchPrefix: true,
    },
    ...(hasEnrolledCourses
      ? [{ type: 'link' as const, to: '/student-portal', label: 'كورساتي وحصصي', icon: GraduationCap }]
      : []),
    { type: 'link', to: '/profile', label: 'حسابي', icon: User },
  ];

  // Use guest nav items during SSR/initial render, switch after mount
  const navItems = mounted && isAuthenticated ? authNavItems : guestNavItems;

  const isItemActive = (item: NavItem) => {
    if (item.type === 'link') {
      return isCurrentPath(item.to, item.matchPrefix);
    }
    if (item.sectionId && location.pathname === '/') {
      return false;
    }
    return false;
  };

  const navShellClass = isHeroOverlay
    ? 'bg-gradient-to-b from-green-950/70 via-green-950/35 to-transparent backdrop-blur-[2px] border-b border-white/10'
    : isScrolled
      ? 'bg-white/95 backdrop-blur-xl border-b-2 border-gold-500/70 shadow-md shadow-green-900/5'
      : 'bg-white/90 backdrop-blur-lg border-b border-green-100/80 shadow-sm';

  const desktopLinkClass = (active: boolean) => {
    const base =
      'relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors duration-200';
    if (isHeroOverlay) {
      return `${base} ${active ? 'text-gold-300' : 'text-white/90 hover:text-white'}`;
    }
    return `${base} ${active ? 'text-green-800' : 'text-gray-600 hover:text-green-800'}`;
  };

  const mobileLinkClass = (active: boolean) =>
    `flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
      active
        ? 'bg-green-800 text-white shadow-md shadow-green-900/15'
        : 'text-gray-700 hover:bg-green-50 hover:text-green-800'
    }`;

  const iconBtnClass = isHeroOverlay
    ? 'border-white/25 bg-white/10 text-white hover:bg-white/20'
    : 'border-green-200/80 bg-white text-green-800 hover:border-gold-400 hover:bg-gold-50/50';

  const renderNavItem = (item: NavItem, variant: 'desktop' | 'mobile', onNavigate?: () => void) => {
    const Icon = item.icon;
    const active = isItemActive(item);
    const className = variant === 'desktop' ? desktopLinkClass(active) : mobileLinkClass(active);

    if (item.type === 'link') {
      return (
        <Link key={item.to} to={item.to} className={className} onClick={onNavigate}>
          {variant === 'mobile' && <Icon className="h-5 w-5 shrink-0" />}
          {item.label}
          {variant === 'desktop' && active && (
            <span className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full bg-gold-500" />
          )}
          {variant === 'mobile' && <ChevronLeft className="mr-auto h-4 w-4 opacity-40" />}
        </Link>
      );
    }

    return (
      <button
        key={item.label}
        type="button"
        onClick={() => {
          item.onClick();
          onNavigate?.();
        }}
        className={className}
      >
        {variant === 'mobile' && <Icon className="h-5 w-5 shrink-0" />}
        {item.label}
        {variant === 'mobile' && <ChevronLeft className="mr-auto h-4 w-4 opacity-40" />}
      </button>
    );
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50" dir="rtl">
        <div className={`transition-all duration-300 ${navShellClass}`}>
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:h-[4.5rem] sm:gap-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <Link
              to={mounted && isAuthenticated ? '/student-portal' : '/'}
              className="group flex min-w-0 shrink-0 items-center gap-2.5 sm:gap-3"
            >
              <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gold-400/30 bg-white/95 p-1 shadow-sm sm:h-11 sm:w-11">
                <img 
                  src={logoImage} 
                  alt={`شعار ${BRAND_NAME} - منصة تعليمية أزهرية للثانوية الأزهرية`}
                  title={BRAND_NAME}
                  className="h-full w-full object-contain logo-transparent" 
                />
              </div>
              <div className="hidden min-w-0 sm:block">
                <p
                  className={`truncate text-sm font-bold font-cairo-bold leading-tight ${
                    isHeroOverlay ? 'text-white' : 'text-green-800'
                  }`}
                >
                  {BRAND_NAME}
                </p>
                <p
                  className={`truncate text-[10px] font-medium leading-tight ${
                    isHeroOverlay ? 'text-gold-200/90' : 'text-gold-600'
                  }`}
                >
                  {BRAND_TAGLINE}
                </p>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden flex-1 items-center justify-center gap-0.5 lg:flex xl:gap-1" aria-label="التنقل الرئيسي">
              {navItems.map((item) => renderNavItem(item, 'desktop'))}
            </nav>

            {/* Actions */}
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <ThemeToggle className={iconBtnClass} />
              {mounted && isAuthenticated ? (
                <>
                  <div className="hidden items-center gap-2 md:flex">
                    <div
                      className={`flex max-w-[140px] items-center gap-2 rounded-full border px-2 py-1 ${
                        isHeroOverlay
                          ? 'border-white/20 bg-white/10'
                          : 'border-green-100 bg-green-50/80'
                      }`}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-green-700 to-gold-500">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <span
                        className={`truncate text-xs font-semibold sm:text-sm ${
                          isHeroOverlay ? 'text-white' : 'text-green-900'
                        }`}
                      >
                        {currentUser?.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={logout}
                      className={`inline-flex min-h-10 items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold transition-all ${
                        isHeroOverlay
                          ? 'border-white/25 text-white hover:bg-white/15'
                          : 'border-green-200 text-green-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600'
                      }`}
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="hidden lg:inline">خروج</span>
                    </button>
                  </div>
                  <Link
                    to="/student-portal"
                    className={`hidden min-h-10 items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold md:inline-flex ${
                      isHeroOverlay
                        ? 'bg-gold-400 text-green-950 hover:bg-gold-300'
                        : 'bg-green-800 text-white hover:bg-green-900'
                    }`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    البوابة
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`hidden min-h-10 items-center rounded-xl px-4 py-2 text-sm font-semibold transition-all sm:inline-flex ${
                      isHeroOverlay
                        ? 'text-white hover:bg-white/10'
                        : 'text-green-800 hover:bg-green-50'
                    }`}
                  >
                    دخول
                  </Link>
                  {!isRegisterPage && (
                    <Link
                      to="/register"
                      className={`inline-flex min-h-10 items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold shadow-sm transition-all sm:px-4 ${
                        isHeroOverlay
                          ? 'bg-gold-400 text-green-950 hover:bg-gold-300'
                          : 'bg-green-800 text-white hover:bg-green-900'
                      }`}
                    >
                      <Sparkles className="h-4 w-4" />
                      سجّل الآن
                    </Link>
                  )}
                </>
              )}

              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-all lg:hidden ${iconBtnClass}`}
                aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" dir="rtl">
          <button
            type="button"
            className="absolute inset-0 bg-green-950/30 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="إغلاق القائمة"
          />
          <div className="absolute inset-x-3 top-16 max-h-[calc(100vh-5rem)] overflow-y-auto rounded-2xl border border-green-100 bg-white shadow-2xl shadow-green-900/10 sm:inset-x-4 sm:top-[4.5rem]">
            <div className="border-b border-green-100 px-4 py-3">
              <p className="text-xs font-semibold text-gold-700">القائمة</p>
              <p className="text-sm font-bold text-green-900">{mounted && isAuthenticated ? 'بوابة الطالب' : 'استكشف المنصة'}</p>
            </div>

            <div className="space-y-1 p-3">
              {navItems.map((item) => renderNavItem(item, 'mobile', () => setIsMobileMenuOpen(false)))}
            </div>

            <div className="space-y-2 border-t border-green-100 p-3">
              <ThemeToggle
                showLabel
                className="border border-green-200 bg-green-50 text-green-800 hover:border-gold-400 hover:bg-gold-50/50"
              />
              {mounted && isAuthenticated ? (
                <>
                  <div className="flex items-center gap-3 rounded-xl bg-green-50 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-green-700 to-gold-500">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-green-900">{currentUser?.name}</p>
                      <p className="text-xs text-gray-500">طالب مسجّل</p>
                    </div>
                  </div>
                  <Link
                    to="/student-portal"
                    className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-800 px-4 py-3 text-sm font-semibold text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="h-5 w-5" />
                    بوابة الطالب
                  </Link>
                  <button
                    type="button"
                    onClick={async () => {
                      await logout();
                      setIsMobileMenuOpen(false);
                      navigate('/', { replace: true });
                    }}
                    className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600"
                  >
                    <LogOut className="h-5 w-5" />
                    تسجيل الخروج
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex min-h-11 items-center justify-center rounded-xl border border-green-200 px-4 py-3 text-sm font-semibold text-green-800"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to="/register"
                    className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-800 px-4 py-3 text-sm font-semibold text-white"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Sparkles className="h-5 w-5" />
                    إنشاء حساب جديد
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
